import { apiClient } from './client';
import type {
  AppRole,
  TeamAuditEntry,
  TeamBrand,
  TeamDetail,
  TeamSummary,
  TeamUser,
} from '@/types';

/** Body for `POST /admin/teams`. */
export interface TeamCreateInput {
  name: string;
}

/** Body for `PUT /admin/teams/{id}` — activation travels with the update. */
export interface TeamUpdateInput {
  name: string;
  isActive: boolean;
}

/** Body for `POST /admin/teams/{id}/brands`. */
export interface TeamAssignBrandInput {
  brandId: number;
  /** Mark as the team's primary brand (at most one per team; 409 otherwise). */
  isPrimary?: boolean;
}

/**
 * Body for `POST /admin/teams/{id}/users/invite` and
 * `POST /my/teams/{id}/users/invite` (backend `InviteUserRequest`).
 * The backend creates the Auth0 user, assigns the role, pre-creates the local
 * `Users` row and adds them to the team in the route.
 */
export interface TeamInviteUserInput {
  email: string;
  /**
   * Display name written to the Auth0 profile. Required for database
   * (external) invites; ignored for Okta invites where Okta owns the profile.
   */
  name?: string;
  /** Admin cannot be assigned via invite. Editors may only assign roles below their own. */
  role?: Exclude<AppRole, 'crm:admin'>;
  /**
   * Auth0 connection derived from the email (see `@/auth/connections`). The
   * backend derives and enforces the same rule; sending it is belt-and-braces.
   * No password is ever sent — database users receive a set-password email.
   */
  connectionName?: string;
}

/**
 * Admin-only team management (crm:admin).
 * Mirrors the backend `TeamsController` under `/admin/teams`:
 * brands and users are assigned/removed individually (no bulk replace), and
 * there are no separate activate/deactivate endpoints.
 */
export const teamsApi = {
  list: async (): Promise<TeamSummary[]> => {
    // The backend returns a plain array (not a paged response).
    const { data } = await apiClient.get<TeamSummary[]>('/admin/teams');
    return data;
  },
  getById: async (id: number): Promise<TeamDetail> => {
    const { data } = await apiClient.get<TeamDetail>(`/admin/teams/${id}`);
    return data;
  },
  create: async (input: TeamCreateInput): Promise<TeamDetail> => {
    const { data } = await apiClient.post<TeamDetail>('/admin/teams', input);
    return data;
  },
  update: async (id: number, input: TeamUpdateInput): Promise<TeamDetail> => {
    const { data } = await apiClient.put<TeamDetail>(`/admin/teams/${id}`, input);
    return data;
  },
  assignBrand: async (id: number, input: TeamAssignBrandInput): Promise<void> => {
    await apiClient.post(`/admin/teams/${id}/brands`, {
      brandId: input.brandId,
      isPrimary: input.isPrimary ?? false,
    });
  },
  /** Set or unset the primary flag on an existing brand assignment. */
  setBrandPrimary: async (id: number, brandId: number, isPrimary: boolean): Promise<void> => {
    await apiClient.put(`/admin/teams/${id}/brands/${brandId}`, { isPrimary });
  },
  removeBrand: async (id: number, brandId: number): Promise<void> => {
    await apiClient.delete(`/admin/teams/${id}/brands/${brandId}`);
  },
  assignUser: async (id: number, userId: number): Promise<void> => {
    await apiClient.post(`/admin/teams/${id}/users`, { userId });
  },
  removeUser: async (id: number, userId: number): Promise<void> => {
    await apiClient.delete(`/admin/teams/${id}/users/${userId}`);
  },
  inviteUser: async (id: number, input: TeamInviteUserInput): Promise<TeamUser> => {
    const { data } = await apiClient.post<TeamUser>(`/admin/teams/${id}/users/invite`, input);
    return data;
  },
  getAudit: async (id: number): Promise<TeamAuditEntry[]> => {
    const { data } = await apiClient.get<TeamAuditEntry[]>(`/admin/teams/${id}/audit`);
    return data;
  },
};

/**
 * Self-service team endpoints for crm:editor and above. Mirrors the backend
 * `MyTeamsController` under `/my/teams`: an editor can list their own teams,
 * see the members of those teams and invite users into them (role strictly
 * below their own rank; the team is fixed by the route, never chosen freely).
 */
export const myTeamsApi = {
  list: async (): Promise<TeamSummary[]> => {
    const { data } = await apiClient.get<TeamSummary[]>('/my/teams');
    return data;
  },
  /**
   * Team detail (brands + users) for one of the caller's teams. Requires
   * crm:editor or above (includes member emails).
   */
  getById: async (id: number): Promise<TeamDetail> => {
    const { data } = await apiClient.get<TeamDetail>(`/my/teams/${id}`);
    return data;
  },
  /**
   * Brands (with primary flag) for one of the caller's teams, no member data.
   * Available to every app role — used by the cascading Brand/Competitors
   * filter so crm:viewer and crm:analyst also get primary-aware options.
   */
  getBrands: async (id: number): Promise<TeamBrand[]> => {
    const { data } = await apiClient.get<TeamBrand[]>(`/my/teams/${id}/brands`);
    return data;
  },
  getUsers: async (id: number): Promise<TeamUser[]> => {
    const { data } = await apiClient.get<TeamUser[]>(`/my/teams/${id}/users`);
    return data;
  },
  inviteUser: async (id: number, input: TeamInviteUserInput): Promise<TeamUser> => {
    const { data } = await apiClient.post<TeamUser>(`/my/teams/${id}/users/invite`, input);
    return data;
  },
  removeUser: async (id: number, userId: number): Promise<void> => {
    await apiClient.delete(`/my/teams/${id}/users/${userId}`);
  },
};
